import React from 'react';
import { cn } from '../../lib/utils';

// Base Skeleton component with shimmer animation
export const Skeleton = ({ className, ...props }) => {
  return (
    <div
      className={cn(
        "animate-pulse rounded-md bg-gray-200",
        className
      )}
      {...props}
    />
  );
};

// Listing Card Skeleton
export const ListingCardSkeleton = () => {
  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden" data-testid="listing-card-skeleton">
      {/* Image placeholder */}
      <Skeleton className="h-56 w-full" />
      
      {/* Content */}
      <div className="p-6 space-y-4">
        {/* Title */}
        <Skeleton className="h-6 w-3/4" />
        
        {/* Location */}
        <div className="flex items-center gap-2">
          <Skeleton className="h-4 w-4 rounded-full" />
          <Skeleton className="h-4 w-1/2" />
        </div>
        
        {/* Price box */}
        <Skeleton className="h-16 w-full rounded-lg" />
        
        {/* Revenue */}
        <div className="flex justify-between">
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="h-4 w-1/4" />
        </div>
        
        {/* Description */}
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-2/3" />
        
        {/* Button */}
        <Skeleton className="h-10 w-full mt-4" />
      </div>
    </div>
  );
};

// Listing Detail Skeleton
export const ListingDetailSkeleton = () => {
  return (
    <div className="min-h-screen bg-gray-50" data-testid="listing-detail-skeleton">
      {/* Header with back button */}
      <div className="bg-white shadow-sm py-4 px-6">
        <Skeleton className="h-10 w-32" />
      </div>
      
      <div className="container mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main content - 2 columns */}
          <div className="lg:col-span-2 space-y-6">
            {/* Image gallery skeleton */}
            <Skeleton className="h-96 w-full rounded-xl" />
            
            {/* Title and badges */}
            <div className="space-y-4">
              <Skeleton className="h-10 w-3/4" />
              <div className="flex gap-2">
                <Skeleton className="h-6 w-24 rounded-full" />
                <Skeleton className="h-6 w-32 rounded-full" />
              </div>
            </div>
            
            {/* Description */}
            <div className="bg-white rounded-xl p-6 space-y-4">
              <Skeleton className="h-6 w-1/4" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
            
            {/* Details grid */}
            <div className="bg-white rounded-xl p-6">
              <Skeleton className="h-6 w-1/3 mb-4" />
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="space-y-2">
                    <Skeleton className="h-4 w-20" />
                    <Skeleton className="h-6 w-full" />
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          {/* Sidebar */}
          <div className="space-y-6">
            {/* Price card */}
            <div className="bg-white rounded-xl p-6 space-y-4">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-12 w-full rounded-lg" />
              <Skeleton className="h-12 w-full rounded-lg" />
            </div>
            
            {/* Agent card */}
            <div className="bg-white rounded-xl p-6 space-y-4">
              <Skeleton className="h-6 w-1/2" />
              <div className="flex items-center gap-4">
                <Skeleton className="h-16 w-16 rounded-full" />
                <div className="space-y-2">
                  <Skeleton className="h-5 w-32" />
                  <Skeleton className="h-4 w-24" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Agent Page Skeleton
export const AgentPageSkeleton = () => {
  return (
    <div className="min-h-screen bg-gray-50" data-testid="agent-page-skeleton">
      {/* Hero section */}
      <div className="bg-gradient-to-r from-[#1e3a8a] to-[#2563eb] py-16">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <Skeleton className="h-40 w-40 rounded-full bg-blue-400/50" />
            <div className="space-y-4 text-center md:text-left">
              <Skeleton className="h-10 w-64 bg-blue-400/50" />
              <Skeleton className="h-6 w-48 bg-blue-400/50" />
              <div className="flex gap-3 justify-center md:justify-start">
                <Skeleton className="h-10 w-32 rounded-lg bg-blue-400/50" />
                <Skeleton className="h-10 w-32 rounded-lg bg-blue-400/50" />
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Listings section */}
      <div className="container mx-auto px-4 py-12">
        <Skeleton className="h-8 w-48 mb-8" />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => (
            <ListingCardSkeleton key={i} />
          ))}
        </div>
      </div>
    </div>
  );
};

// Team Members Grid Skeleton
export const TeamGridSkeleton = () => {
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="team-grid-skeleton">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="bg-white rounded-xl overflow-hidden shadow-lg">
          <Skeleton className="h-64 w-full" />
          <div className="p-4 space-y-3">
            <Skeleton className="h-6 w-3/4 mx-auto" />
            <Skeleton className="h-4 w-1/2 mx-auto" />
            <div className="flex justify-center gap-2">
              <Skeleton className="h-8 w-8 rounded-full" />
              <Skeleton className="h-8 w-8 rounded-full" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Testimonials Skeleton
export const TestimonialsSkeleton = () => {
  return (
    <div className="grid md:grid-cols-3 gap-6" data-testid="testimonials-skeleton">
      {[...Array(3)].map((_, i) => (
        <div key={i} className="bg-white rounded-xl p-6 shadow-lg space-y-4">
          <div className="flex gap-1">
            {[...Array(5)].map((_, j) => (
              <Skeleton key={j} className="h-5 w-5" />
            ))}
          </div>
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-2/3" />
          <div className="flex items-center gap-3 pt-4">
            <Skeleton className="h-12 w-12 rounded-full" />
            <div className="space-y-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-3 w-32" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Hero Section Skeleton
export const HeroSkeleton = () => {
  return (
    <div className="relative h-[600px] bg-gray-200 animate-pulse" data-testid="hero-skeleton">
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="text-center space-y-6 px-4">
          <Skeleton className="h-16 w-96 max-w-full mx-auto" />
          <Skeleton className="h-8 w-80 max-w-full mx-auto" />
          <div className="flex gap-4 justify-center">
            <Skeleton className="h-12 w-40 rounded-lg" />
            <Skeleton className="h-12 w-40 rounded-lg" />
          </div>
        </div>
      </div>
    </div>
  );
};

// Page Loading Skeleton (full page)
export const PageLoadingSkeleton = () => {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="page-loading-skeleton">
      <div className="text-center space-y-4">
        <div className="w-16 h-16 border-4 border-[#1e3a8a] border-t-transparent rounded-full animate-spin mx-auto"></div>
        <p className="text-gray-600 animate-pulse">Loading...</p>
      </div>
    </div>
  );
};

export default Skeleton;
